import React from 'react';

/** LLM (or rule-based) insights for a completed test report (ROO-131). */
export interface ReportSuggestion {
  title: string;
  detail: string;
}
export interface ReportAnalysis {
  summary?: string;
  strengths?: string[];
  improvements?: string[];
  suggestions?: ReportSuggestion[];
  source?: 'llm' | 'rule-based';
}

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #ececec',
  borderRadius: 14,
  padding: '16px 18px',
  marginTop: 14,
};
const heading: React.CSSProperties = { margin: '0 0 8px', fontSize: 14, fontWeight: 700, color: '#1f2937' };

function Chips({ items, tone }: { items: string[]; tone: 'good' | 'work' }) {
  const bg = tone === 'good' ? '#e8f6ee' : '#fdf3e7';
  const fg = tone === 'good' ? '#1f7a4d' : '#9a6a16';
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: 14, lineHeight: 1.45, color: '#374151' }}>
          <span style={{ display: 'inline-block', background: bg, color: fg, borderRadius: 6, padding: '1px 7px', marginRight: 8, fontSize: 12, fontWeight: 700 }}>
            {tone === 'good' ? '✓' : '↑'}
          </span>
          {t}
        </li>
      ))}
    </ul>
  );
}

export default function ReportInsights({ analysis }: { analysis?: ReportAnalysis | null }) {
  if (!analysis) return null;
  const { summary, strengths = [], improvements = [], suggestions = [] } = analysis;
  if (!summary && !strengths.length && !improvements.length && !suggestions.length) return null;

  return (
    <div>
      {summary && (
        <div style={{ ...card, background: '#f7faf8' }}>
          <div style={heading}>Your insights</div>
          <p style={{ margin: 0, fontSize: 14.5, lineHeight: 1.6, color: '#374151' }}>{summary}</p>
        </div>
      )}

      {strengths.length > 0 && (
        <div style={card}>
          <div style={heading}>Strengths</div>
          <Chips items={strengths} tone="good" />
        </div>
      )}

      {improvements.length > 0 && (
        <div style={card}>
          <div style={heading}>Areas to improve</div>
          <Chips items={improvements} tone="work" />
        </div>
      )}

      {suggestions.length > 0 && (
        <div style={card}>
          <div style={heading}>Suggested next steps</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {suggestions.map((s, i) => (
              <div key={i} style={{ borderLeft: `3px solid ${COLOR}`, paddingLeft: 12 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{s.title}</div>
                {s.detail && <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#4b5563', marginTop: 2 }}>{s.detail}</div>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const COLOR = '#2f8f5b';
